import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ProgressMeter } from "@msuite/ui/progress-meter";
import { OrderStateTag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { formatNumber, getProgress } from "@msuite/utils/numbers";
import { getSuborderNumberString } from "@msuite/utils/stock";
import { guardString } from "@msuite/utils/strings";
import { useSuborder } from "@/hooks/use-suborder";
import type { SuborderPanelBaseProps } from "../../shared";

export function SuborderInformation({ orderId, suborderId }: SuborderPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { suborder } = useSuborder(orderId, suborderId);

	/** Render */
	return (
		<ContentCard
			title={t("general.information")}
			icon={icon("Info")}
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("stock.suborder_id")}</DataGrid.Label>
					<DataGrid.Value>
						{getSuborderNumberString({
							orderNumber: suborder?.orderNumber,
							suborderNumber: suborder?.number,
						})}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general.state")}</DataGrid.Label>
					<DataGrid.Value>
						{suborder ? (
							<OrderStateTag
								size="md"
								state={suborder.state}
							/>
						) : (
							guardString(null)
						)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("stock.warehouse_zone")}</DataGrid.Label>
					<DataGrid.Value>{guardString(suborder?.warehouseZoneName)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general.quantity")}</DataGrid.Label>
					<DataGrid.Value>{formatNumber(suborder?.quantity, "integer")}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("stock.picked_quantity")}</DataGrid.Label>
					<DataGrid.Value>{formatNumber(suborder?.pickedQuantity, "integer")}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("stock.open_quantity")}</DataGrid.Label>
					<DataGrid.Value>{formatNumber(suborder?.openQuantity, "integer")}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general.progress")}</DataGrid.Label>
					<DataGrid.Cell>
						<ProgressMeter value={getProgress(suborder?.quantity, suborder?.pickedQuantity)} />
					</DataGrid.Cell>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general_fields.updated_by")}</DataGrid.Label>
					<DataGrid.Value>{guardString(suborder?.updatedByName)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("general_fields.updated_at")}</DataGrid.Label>
					<DataGrid.Value>
						{suborder?.updatedAt ? moment(suborder.updatedAt).longFormat() : guardString(null)}
					</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
		</ContentCard>
	);
}
